import {
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Modal,
  TextField,
  Fab,
  Box,
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { BACKEND_URL, DEFAULT_SCENE } from '../constants';
import { useDeleteScene, useFetchScenes } from '../hooks/useScenes';
import { CreateSceneRequest } from '../types/types';
import { Header, HeaderActionType } from '../Components/Header';
import { isTokenValid } from '../helpers';
import { ConfirmModal } from "../Components/Modals/ConfirmModal";
import { useGetAssetData } from "../hooks/useAsset";


enum DashboardModalState {
  SCENE_ADD,
  CONFIRM_DELETE,
};

export const Dashboard = () => {
  const navigate = useNavigate();
  const [modalState, setModalState] = useState<DashboardModalState | undefined>(undefined);
  const [sceneName, setSceneName] = useState<string>("");
  const { data: scenes, refetch } = useFetchScenes();
  const isLoggedIn = useMemo(() => isTokenValid(), [localStorage.getItem("jwt")]);
  const { deleteFn } = useDeleteScene();
  const [selectedSceneId, setSelectedSceneId] = useState<string | undefined>();
  const { fetch: getAsset } = useGetAssetData();
  const [sceneThumbnailMapping, setSceneThumbnailMapping] = useState<{ [key: string]: string }>({});
  const [mounted, setIsMounted] = useState<boolean>(false);

  useEffect(() => {
    if (scenes && !mounted) {
      const mapping: { [key: string]: string } = {};
      // console.log(scenes);
      setIsMounted(true);

      // TODO - convert these to promise.All but eh for now
      const fetchSceneThumbnails = async () => {
        for (const s of scenes) {
          if (!s.thumbnailId) {
            continue;
          }
          const data = await getAsset(`/assets/${s.thumbnailId}`);
          const blob = new Blob([data], { type: "image/png" });
          mapping[s.id] = URL.createObjectURL(blob);
        }

        setSceneThumbnailMapping(mapping);
      }

      fetchSceneThumbnails();
    }
  }, [scenes, sceneThumbnailMapping, mounted, setIsMounted, getAsset]);

  const createNewScene = async (request: CreateSceneRequest) => {
    try {
      // read auth token from localstorage
      const token = localStorage.getItem("jwt");
      const response = await fetch(`${BACKEND_URL}/scenes`, {
        method: "POST",
        headers: {
          "Authorization": token ?? '',
        },
        body: JSON.stringify(request),
      });
      await response.json();
    } catch (err) {
      console.error(err)
    }
  };

  return (
    <Grid
      sx={(theme) => ({
        backgroundColor: theme.palette.background.default,
      })}
    >
      <ConfirmModal
        open={modalState === DashboardModalState.CONFIRM_DELETE}
        onCancel={async () => {
          await refetch();
          setModalState(undefined);
        }}
        onConfirm={async () => {
          if (!selectedSceneId) {
            return;
          }
          await deleteFn(selectedSceneId);
          await refetch();
          setModalState(undefined);
        }}
        title="Scene Delete"
        message="Are you sure you want to delete this scene?"
      />
      <Header
        isLoggedIn={isLoggedIn}
        activeActions={[HeaderActionType.WORKSPACE, HeaderActionType.LOGIN, HeaderActionType.ADMIN]}
        drawerOpen={false}
      />

      <Grid
        container
        spacing={4}
        direction="column"
        justifyContent="center"
        alignItems="center"
        style={{ minHeight: '100vh' }}
      >

        {/* Title + Create button */}
        <Grid container justifyContent="center" spacing={2} mb={4}>
          <Grid size={12}>
            <Typography
              align="center"
              variant="h4"
              color="primary"
              gutterBottom
            >
              Group Scenes
            </Typography>
          </Grid>
        </Grid>
        <Fab
          sx={(theme) => ({
            backgroundColor: theme.palette.primary.main,
            position: "fixed",
            bottom: 16,  // distance from bottom
            right: 16,   // distance from right
            zIndex: 1000, // keep above most content
          })}
          onClick={() => {
            setModalState(DashboardModalState.SCENE_ADD);
          }}
        >
          <AddIcon />
        </Fab>

        {/* Scenes grid */}
        <Grid container spacing={3} justifyContent="center">
          {scenes?.map((s) => (
            <Grid key={s.id} size={12}>
              <Card
                variant="outlined"
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  marginLeft: '1em',
                  marginRight: '1em'
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  {sceneThumbnailMapping[s.id] && (
                    <Box
                      sx={{
                        height: "20vh",            // fixed height to match aspect ratio
                        mb: 1,                 // margin-bottom
                        backgroundColor: "#eee",
                        overflow: "hidden",
                        borderRadius: 1,       // optional, rounded corners
                      }}
                    >
                      <img
                        src={sceneThumbnailMapping[s.id]}
                        alt={s.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover", // fills the box while keeping aspect ratio
                          display: "block",
                        }}
                      />
                    </Box>
                  )}
                  <Typography variant="h6" gutterBottom>
                    {s.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Created on {new Date(s.createdAt).toLocaleDateString()} at{" "}
                    {new Date(s.createdAt).toLocaleTimeString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Last updated on {new Date(s.updatedAt).toLocaleDateString()} at{" "}
                    {new Date(s.updatedAt).toLocaleTimeString()}
                  </Typography>
                </CardContent>
                <CardActions sx={{ flexDirection: "column", p: 0 }}>
                  <Button
                    sx={{ borderRadius: 0 }}
                    onClick={() => {
                      const params = new URLSearchParams({ sceneId: s.id });
                      navigate(`/workspace?${params.toString()}`);
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    sx={{ borderRadius: 0 }}
                    color="error"
                    onClick={async () => {
                      setSelectedSceneId(s.id);
                      setModalState(DashboardModalState.CONFIRM_DELETE);
                    }}
                  >
                    Delete
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Grid>

      {/* Add modal */}
      <Modal
        open={modalState === DashboardModalState.SCENE_ADD}
        onClose={() => {
          setModalState(undefined);
          setSceneName('');
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Grid
          sx={(theme) => ({
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
            padding: 3,
            minWidth: 400,
            boxShadow: 24,
            outline: "none",
            display: "flex",
            flexDirection: "row",   // stack vertically
            gap: 4,                 // space between children
          })}
        >
          <TextField
            type="text"
            value={sceneName}
            label="Scene Name"
            onChange={(e) => setSceneName(e.target.value)}
          />
          <Button
            disabled={!sceneName}
            variant="contained"
            onClick={async () => {
              await createNewScene({ name: sceneName, contents: DEFAULT_SCENE });
              setModalState(undefined);
              await refetch();
              setSceneName('');
            }}
          >
            Submit
          </Button>
        </Grid>
      </Modal>
    </Grid>
  )
}
